import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { nikaidoService } from '@/services/nikaido.service';

export const AgentWorkflowPage: React.FC = () => {
  const { agentId, workflowId } = useParams<{ agentId: string; workflowId: string }>();
  const navigate = useNavigate();
  const [embedUrl, setEmbedUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadEditor = async () => {
      if (!workflowId) return;
      setIsLoading(true);
      try {
        const url = await nikaidoService.getWorkflowEmbedUrl(workflowId);
        if (mounted) setEmbedUrl(url);
      } catch {
        toast.error('Failed to open workflow editor');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadEditor();
    return () => {
      mounted = false;
    };
  }, [workflowId]);

  return (
    <div className="h-screen w-screen bg-white flex flex-col overflow-hidden">
      <div className="h-12 flex items-center gap-3 px-4 border-b border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => navigate(agentId ? `/agents/${agentId}` : '/agents')}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">Workflow editor</p>
          {workflowId && <p className="text-xs text-gray-400 truncate">{workflowId}</p>}
        </div>
      </div>

      <div className="flex-1 min-h-0">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Opening workflow editor...
          </div>
        ) : embedUrl ? (
          <iframe
            title="Nikaido workflow editor"
            src={embedUrl}
            className="w-full h-full border-0"
            allow="clipboard-read; clipboard-write"
          />
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">
            Workflow editor is unavailable.
          </div>
        )}
      </div>
    </div>
  );
};
